/**
 * Calculate daily send quotas for touchpoint drip scheduling
 * Ensures 25-day minimum spacing between messages to same client
 * Now includes timezone-aware scheduling
 */

import { isWithinSMSSchedule, getNextAvailableSendTime, generateOptimalSendTimes, getOptimalTimeSlots, getAgentCurrentTime, normalizeScheduleDays, normalizeTimeFormat } from '../services/timezone-location-service.js';

/**
 * Today's date in agent timezone as YYYY-MM-DD (for send_after_date comparisons).
 * @param {string} timezone
 * @param {Date} [now]
 */
export function getAgentTodayDateString(timezone = 'America/New_York', now = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const y = parts.find(p => p.type === 'year')?.value;
    const m = parts.find(p => p.type === 'month')?.value;
    const d = parts.find(p => p.type === 'day')?.value;
    return `${y}-${m}-${d}`;
  } catch {
    return now.toISOString().substring(0, 10);
  }
}

const WEEKDAY_TO_ISO = { Sun: 7, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/**
 * Wall-clock parts of `date` in `timeZone`. Weekday is 1=Mon … 7=Sun.
 * @param {Date} date
 * @param {string} timeZone
 */
function getZonedParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const map = {};
  for (const p of parts) map[p.type] = p.value;
  let hour = map.hour;
  if (hour === '24') hour = '00';
  return {
    weekday: WEEKDAY_TO_ISO[map.weekday] || 1,
    hhmm: `${String(hour).padStart(2, '0')}:${String(map.minute).padStart(2, '0')}`,
    year: parseInt(map.year, 10),
    month: parseInt(map.month, 10),
    day: parseInt(map.day, 10),
    hour: parseInt(hour, 10),
    minute: parseInt(map.minute, 10),
    second: parseInt(map.second, 10),
  };
}

/** Offset of `timeZone` at `date`: local-as-UTC minus actual UTC, in ms. */
function getTimeZoneOffsetMs(date, timeZone) {
  const z = getZonedParts(date, timeZone);
  const asUtc = Date.UTC(z.year, z.month - 1, z.day, z.hour, z.minute, z.second);
  return asUtc - date.getTime();
}

/**
 * UTC instant for a wall-clock time in `timeZone`.
 * @returns {Date}
 */
function zonedLocalToUtc(year, month, day, hour, minute, timeZone) {
  let utc = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 3; i++) {
    const next = Date.UTC(year, month - 1, day, hour, minute, 0) - getTimeZoneOffsetMs(new Date(utc), timeZone);
    if (next === utc) break;
    utc = next;
  }
  return new Date(utc);
}

function addCalendarDays(year, month, day, days) {
  const dt = new Date(Date.UTC(year, month - 1, day + days));
  return {
    year: dt.getUTCFullYear(),
    month: dt.getUTCMonth() + 1,
    day: dt.getUTCDate(),
  };
}

/**
 * Start (inclusive) and end (exclusive) of the agent's local calendar day, as UTC Dates.
 * @param {string} timezone
 * @param {Date} [now]
 * @returns {{ start: Date, end: Date }}
 */
export function getAgentLocalDayBounds(timezone = 'UTC', now = new Date()) {
  const today = getAgentTodayDateString(timezone, now);
  const [year, month, day] = today.split('-').map(Number);
  const next = addCalendarDays(year, month, day, 1);
  return {
    start: zonedLocalToUtc(year, month, day, 0, 0, timezone),
    end: zonedLocalToUtc(next.year, next.month, next.day, 0, 0, timezone),
  };
}

/**
 * How many cron slots from the current run through the end of the agent's local
 * day fall inside the SMS schedule. Bounds match isWithinSMSSchedule
 * (start inclusive, end inclusive) so the batch split counts every run that
 * will actually send.
 *
 * @param {Object} params
 * @param {string} params.timezone
 * @param {string} [params.startTime]
 * @param {string} [params.endTime]
 * @param {number[]} [params.allowedDays]
 * @param {number} [params.cronIntervalHours]
 * @param {Date} [params.now]
 * @returns {number}
 */
export function countRemainingScheduledRuns({
  timezone = 'America/New_York',
  startTime = '09:00',
  endTime = '17:00',
  allowedDays = [1, 2, 3, 4, 5],
  cronIntervalHours = 1,
  now = new Date(),
} = {}) {
  const intervalMs = Math.max(1, cronIntervalHours) * 60 * 60 * 1000;
  const firstSlotMs = Math.floor(now.getTime() / intervalMs) * intervalMs;
  const { end } = getAgentLocalDayBounds(timezone, now);
  const normalizedStart = normalizeTimeFormat(startTime);
  const normalizedEnd = normalizeTimeFormat(endTime);
  const normalizedDays = normalizeScheduleDays(allowedDays);

  let count = 0;
  for (let t = firstSlotMs; t < end.getTime(); t += intervalMs) {
    const zoned = getZonedParts(new Date(t), timezone);
    if (!normalizedDays.includes(zoned.weekday)) continue;
    if (zoned.hhmm >= normalizedStart && zoned.hhmm <= normalizedEnd) count++;
  }
  return count;
}

/** Compare YYYY-MM-DD strings */
export function compareDateStrings(a, b) {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** max(a, b) for YYYY-MM-DD */
export function maxDateString(a, b) {
  return compareDateStrings(a, b) >= 0 ? a : b;
}

/**
 * Count allowed weekdays between two inclusive calendar dates (YYYY-MM-DD).
 */
export function countBusinessDaysInRange(startYmd, endYmd, allowedDays = [1, 2, 3, 4, 5]) {
  if (!startYmd || !endYmd || compareDateStrings(startYmd, endYmd) > 0) return 0;

  const normalizedDays = normalizeScheduleDays(allowedDays);

  const [sy, sm, sd] = startYmd.split('-').map(Number);
  const [ey, em, ed] = endYmd.split('-').map(Number);
  const current = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  let count = 0;

  while (current <= end) {
    const dayOfWeek = current.getDay() || 7;
    if (normalizedDays.includes(dayOfWeek)) count++;
    current.setDate(current.getDate() + 1);
  }

  return count;
}

/**
 * Last calendar day of month for monthYear (YYYY-MM).
 */
export function getMonthEndDateString(monthYear) {
  const year = parseInt(monthYear.split('-')[0], 10);
  const month = parseInt(monthYear.split('-')[1], 10);
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
}

/**
 * Calculate daily send quota for a given month
 * @param {number} eligibleClientCount - Number of clients eligible to receive messages
 * @param {string} monthYear - Format: '2025-01'
 * @returns {Object} {dailyQuota: number, totalDays: number, estimatedCompletion: string}
 */
export function calculateDailyQuota(eligibleClientCount, monthYear) {
  // Get number of days in the month
  const year = parseInt(monthYear.split('-')[0]);
  const month = parseInt(monthYear.split('-')[1]) - 1; // JavaScript months are 0-indexed
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  // Calculate daily quota
  const dailyQuota = Math.ceil(eligibleClientCount / daysInMonth);
  
  // Calculate estimated completion
  const daysToComplete = Math.ceil(eligibleClientCount / dailyQuota);
  const estimatedCompletion = `${daysToComplete} days`;
  
  return {
    dailyQuota,
    totalDays: daysInMonth,
    estimatedCompletion,
    eligibleClientCount
  };
}

/**
 * Generate SQL query to get eligible clients for touchpoint sending
 * @param {string} agentId - Agent's UUID
 * @returns {string} SQL query string
 */
export function getEligibleClientsQuery(agentId) {
  return `
    SELECT 
      id,
      name,
      phone,
      last_touchpoint_sent_at,
      CASE 
        WHEN last_touchpoint_sent_at IS NULL THEN 'never'
        ELSE EXTRACT(DAYS FROM CURRENT_DATE - last_touchpoint_sent_at)::text || ' days ago'
      END as last_sent_display
    FROM clients 
    WHERE agent_id = $1 
      AND phone IS NOT NULL 
      AND phone != ''
      AND (last_touchpoint_sent_at IS NULL 
           OR last_touchpoint_sent_at <= CURRENT_DATE - INTERVAL '25 days')
    ORDER BY last_touchpoint_sent_at ASC NULLS FIRST
  `;
}

/**
 * Generate SQL query to get clients for today's batch
 * @param {string} agentId - Agent's UUID
 * @param {number} limit - Number of clients to select
 * @returns {string} SQL query string
 */
export function getTodaysBatchQuery(agentId, limit) {
  return `
    SELECT 
      id,
      name,
      phone,
      last_touchpoint_sent_at
    FROM clients 
    WHERE agent_id = $1 
      AND phone IS NOT NULL 
      AND phone != ''
      AND (last_touchpoint_sent_at IS NULL 
           OR last_touchpoint_sent_at <= CURRENT_DATE - INTERVAL '25 days')
    ORDER BY last_touchpoint_sent_at ASC NULLS FIRST
    LIMIT $2
  `;
}

/**
 * Generate SQL query to update client's last touchpoint sent date
 * @param {string} clientId - Client's UUID
 * @returns {string} SQL query string
 */
export function updateClientLastSentQuery(clientId) {
  return `
    UPDATE clients 
    SET last_touchpoint_sent_at = CURRENT_TIMESTAMP,
        updated_at = CURRENT_TIMESTAMP
    WHERE id = $1
  `;
}

/**
 * Calculate progress for current month's drip
 * @param {number} totalClients - Total number of clients
 * @param {number} sentToday - Number of messages sent today
 * @param {number} daysElapsed - Number of days elapsed in month
 * @param {string} monthYear - Current month-year
 * @returns {Object} Progress information
 */
export function calculateProgress(totalClients, sentToday, daysElapsed, monthYear) {
  const year = parseInt(monthYear.split('-')[0]);
  const month = parseInt(monthYear.split('-')[1]) - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  const dailyQuota = Math.ceil(totalClients / daysInMonth);
  const totalSent = sentToday * daysElapsed;
  const remaining = Math.max(0, totalClients - totalSent);
  const progressPercentage = Math.min(100, (totalSent / totalClients) * 100);
  
  return {
    totalClients,
    totalSent,
    remaining,
    dailyQuota,
    progressPercentage: Math.round(progressPercentage),
    daysElapsed,
    daysInMonth,
    estimatedCompletion: remaining > 0 ? Math.ceil(remaining / dailyQuota) : 0
  };
}

/**
 * Format month-year for display
 * @param {string} monthYear - Format: '2025-01'
 * @returns {string} Formatted string like 'January 2025'
 */
export function formatMonthYear(monthYear) {
  const year = parseInt(monthYear.split('-')[0]);
  const month = parseInt(monthYear.split('-')[1]) - 1;
  const date = new Date(year, month);
  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
}

/**
 * Get current month-year and next 2 months
 * @returns {string[]} Array of month-year strings
 */
export function getCurrentAndNextTwoMonths() {
  const current = new Date();
  const months = [];
  
  for (let i = 0; i < 3; i++) {
    const date = new Date(current.getFullYear(), current.getMonth() + i, 1);
    const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    months.push(monthYear);
  }
  
  return months;
}

/**
 * Calculate optimal send times for a batch of clients with timezone awareness
 * Uses research-based optimal time slots for maximum SMS engagement
 * @param {Array} clients - Array of clients
 * @param {string} agentTimezone - Agent's timezone
 * @param {string} startTime - Start time (HH:MM format)
 * @param {string} endTime - End time (HH:MM format)
 * @param {number[]} allowedDays - Array of allowed days (1=Monday, 7=Sunday)
 * @param {number} batchSize - Number of clients to schedule
 * @returns {Array} Array of clients with optimized send times
 */
export function scheduleClientBatch(clients, agentTimezone, startTime = '09:00', endTime = '17:00', allowedDays = [1,2,3,4,5], batchSize = 10) {
  // Use the optimal time slot generation for better engagement
  const clientsToSchedule = clients.slice(0, batchSize);
  return generateOptimalSendTimes(clientsToSchedule, agentTimezone, startTime, endTime, allowedDays);
}

/**
 * Legacy function for backward compatibility - now uses optimal scheduling
 * @param {Array} clients - Array of clients
 * @param {string} agentTimezone - Agent's timezone
 * @param {string} startTime - Start time (HH:MM format)
 * @param {string} endTime - End time (HH:MM format)
 * @param {number[]} allowedDays - Array of allowed days (1=Monday, 7=Sunday)
 * @param {number} batchSize - Number of clients to schedule
 * @returns {Array} Array of clients with scheduled send times
 */
export function scheduleClientBatchLegacy(clients, agentTimezone, startTime = '09:00', endTime = '17:00', allowedDays = [1,2,3,4,5], batchSize = 10) {
  const scheduledClients = [];
  const totalMinutes = (parseInt(endTime.split(':')[0]) - parseInt(startTime.split(':')[0])) * 60 + 
                       (parseInt(endTime.split(':')[1]) - parseInt(startTime.split(':')[1]));
  const intervalMinutes = Math.floor(totalMinutes / batchSize);
  
  let currentTime = getNextAvailableSendTime(agentTimezone, startTime, endTime, allowedDays);
  
  for (let i = 0; i < Math.min(clients.length, batchSize); i++) {
    scheduledClients.push({
      ...clients[i],
      scheduledSendTime: new Date(currentTime),
      sendOrder: i + 1
    });
    
    // Add interval between sends
    currentTime = new Date(currentTime.getTime() + (intervalMinutes * 60 * 1000));
    
    // If we've gone past end time, move to next day
    const agentTime = new Date(currentTime.toLocaleString('en-US', { timeZone: agentTimezone }));
    const currentTimeStr = agentTime.toTimeString().substring(0, 5);
    
    if (currentTimeStr > endTime) {
      currentTime = getNextAvailableSendTime(agentTimezone, startTime, endTime, allowedDays);
    }
  }
  
  return scheduledClients;
}

/**
 * Check if it's appropriate to send SMS now for an agent
 * @param {string} agentTimezone - Agent's timezone
 * @param {string} startTime - Start time (HH:MM format)
 * @param {string} endTime - End time (HH:MM format)
 * @param {number[]} allowedDays - Array of allowed days
 * @returns {boolean} Whether it's okay to send now
 */
export function canSendSMSNow(agentTimezone, startTime = '09:00', endTime = '17:00', allowedDays = [1,2,3,4,5]) {
  return isWithinSMSSchedule(agentTimezone, startTime, endTime, allowedDays);
}

/**
 * Calculate timezone-aware daily quota considering business hours
 * @param {number} eligibleClientCount - Number of clients eligible to receive messages
 * @param {string} monthYear - Format: '2025-01'
 * @param {string} timezone - Agent's timezone
 * @param {string} startTime - Start time (HH:MM format)
 * @param {string} endTime - End time (HH:MM format)
 * @param {number[]} allowedDays - Array of allowed days
 * @returns {Object} Enhanced quota calculation with timezone awareness
 */
export function calculateTimezoneAwareQuota(
  eligibleClientCount,
  monthYear,
  timezone,
  startTime = '09:00',
  endTime = '17:00',
  allowedDays = [1, 2, 3, 4, 5],
  sendAfterDate = null,
  referenceToday = null,
  sendBeforeDate = null,
  quotaLockToFullWindow = false
) {
  const year = parseInt(monthYear.split('-')[0], 10);
  const month = parseInt(monthYear.split('-')[1], 10) - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const monthEnd = getMonthEndDateString(monthYear);
  const today = referenceToday || getAgentTodayDateString(timezone);

  const businessDaysInMonth = countBusinessDaysInMonth(year, month, allowedDays);

  let effectiveEnd = monthEnd;
  if (sendBeforeDate && compareDateStrings(sendBeforeDate, effectiveEnd) < 0) {
    effectiveEnd = sendBeforeDate;
  }

  // Spread sends across business days from effective start through effective end.
  let effectiveStart = monthStart;
  if (sendAfterDate && compareDateStrings(sendAfterDate, monthStart) > 0) {
    effectiveStart = sendAfterDate;
  }
  if (quotaLockToFullWindow && sendAfterDate) {
    effectiveStart = sendAfterDate;
  } else if (compareDateStrings(today, effectiveStart) > 0) {
    effectiveStart = today;
  } else if (sendAfterDate && compareDateStrings(today, sendAfterDate) < 0) {
    effectiveStart = sendAfterDate;
  }

  if (compareDateStrings(effectiveStart, effectiveEnd) > 0) {
    effectiveStart = effectiveEnd;
  }

  const businessDaysRemaining = countBusinessDaysInRange(effectiveStart, effectiveEnd, allowedDays);
  const businessDaysInWindow = quotaLockToFullWindow && sendAfterDate && sendBeforeDate
    ? countBusinessDaysInRange(sendAfterDate, sendBeforeDate, allowedDays)
    : businessDaysRemaining;
  const divisor = Math.max(1, quotaLockToFullWindow ? businessDaysInWindow : businessDaysRemaining);
  const dailyQuota = eligibleClientCount > 0 ? Math.ceil(eligibleClientCount / divisor) : 0;
  const daysToComplete = dailyQuota > 0 ? Math.ceil(eligibleClientCount / dailyQuota) : 0;

  return {
    dailyQuota,
    totalDays: daysInMonth,
    businessDays: businessDaysInMonth,
    businessDaysRemaining,
    businessDaysInWindow: quotaLockToFullWindow ? businessDaysInWindow : businessDaysRemaining,
    effectiveStart,
    effectiveEnd,
    quotaLockToFullWindow,
    sendBeforeDate: sendBeforeDate || null,
    estimatedCompletion: `${daysToComplete} business days`,
    eligibleClientCount,
    timezone,
    schedule: {
      startTime,
      endTime,
      allowedDays: allowedDays.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d - 1] || ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]),
    },
  };
}

/**
 * Count business days in a given month
 * @param {number} year - Year
 * @param {number} month - Month (0-indexed)
 * @param {number[]} allowedDays - Array of allowed days (1=Monday, 7=Sunday)
 * @returns {number} Number of business days
 */
function countBusinessDaysInMonth(year, month, allowedDays) {
  const normalizedDays = normalizeScheduleDays(allowedDays);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  let businessDays = 0;
  
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay() || 7; // Convert Sunday (0) to 7
    if (normalizedDays.includes(dayOfWeek)) {
      businessDays++;
    }
  }
  
  return businessDays;
}

/**
 * Get optimal time slot recommendations for an agent
 * @param {string} timezone - Agent's timezone
 * @param {string} startTime - Start time (HH:MM format)
 * @param {string} endTime - End time (HH:MM format)
 * @param {number[]} allowedDays - Array of allowed days
 * @returns {Object} Optimal time slot recommendations
 */
export function getOptimalTimeSlotRecommendations(timezone, startTime = '09:00', endTime = '17:00', allowedDays = [1,2,3,4,5]) {
  const optimalSlots = getOptimalTimeSlots(timezone, startTime, endTime, allowedDays);
  const currentTime = getAgentCurrentTime(timezone);
  
  return {
    timezone,
    currentTime: currentTime.timeString,
    isBusinessHours: currentTime.isBusinessHours,
    optimalSlots,
    recommendations: [
      'Morning peak (9:00-10:30): Highest SMS engagement rates',
      'Afternoon peak (2:00-3:30): Excellent for follow-ups and responses',
      'Late morning (11:00-12:00): Good for informational messages',
      'End of day (4:00-5:00): Effective for closing conversations',
      'Avoid lunch hours (12:00-1:00) for better response rates',
      'Space messages 15-30 minutes apart to avoid overwhelming clients'
    ],
    engagementTips: [
      'Messages sent during optimal times have 40% higher open rates',
      'Afternoon messages get the fastest response times',
      'Morning messages are best for important announcements',
      'End-of-day messages work well for gentle follow-ups'
    ]
  };
}
